<div class="absolute inset-0 flex min-w-0 flex-col overflow-hidden">
    <mat-drawer-container class="bg-card h-full flex-auto dark:bg-transparent">
        <mat-drawer-content class="flex flex-col">
            <!-- Main Content -->
            <div class="flex flex-auto flex-col">
                <!-- Header -->
                <div class="bg-default flex flex-col items-start px-6 py-8 sm:flex-row sm:items-center sm:justify-between md:px-8">
                    <!-- Title -->
                    <div>
                        <div class="text-4xl font-extrabold leading-none tracking-tight">Terms & Conditions</div>
                        <div class="text-secondary ml-0.5 font-medium">
                            <span>Effective Date: 03 September 2025</span>
                        </div>
                    </div>
                    
                    <!-- Quick Navigation for Mobile -->
                    <div class="mt-4 sm:mt-0">
                        <button
                            mat-stroked-button
                            (click)="scrollToSection('acceptance-section')"
                            class="ml-3"
                        >
                            <mat-icon>arrow_downward</mat-icon>
                            <span class="ml-2">Jump to Acceptance</span>
                        </button>
                    </div>
                </div>

                <!-- Policy Content -->
                <div class="flex-1 overflow-y-auto px-6 py-4 md:px-8">
                    <mat-card class="max-w-4xl mx-auto !bg-white shadow-lg">
                        <div class="p-6 md:p-8 space-y-6">
                            <!-- Introduction -->
                            <div class="prose max-w-none">
                                <p class="text-lg text-gray-700 leading-relaxed">
                                    These Terms & Conditions ("Terms") govern your use of the Bookletics application ("App"), 
                                    website, and services ("Platform"), operated by Bookletics International FZCO, registered 
                                    under the laws of the United Arab Emirates, with its principal office at Building A1, Dubai 
                                    Digital Park, Dubai Silicon Oasis, Dubai, United Arab Emirates ("Bookletics", "we", "us", "our").
                                </p>
                            </div>

                            <!-- Quick Navigation Links -->
                            <div class="bg-gray-50 rounded-lg p-4 mb-6">
                                <h3 class="font-semibold text-gray-800 mb-3">Quick Navigation</h3>
                                <div class="flex flex-wrap gap-10">
                                    <button 
                                        *ngFor="let section of [1,2,3,4,5]"
                                        mat-raised-button 
                                        color="primary"
                                        class="!text-xs"
                                        (click)="scrollToSection('section-' + section)"
                                    >
                                         {{section}}
                                    </button>
                                </div>
                            </div>

                            <!-- Section 1: Eligibility -->
                            <div id="section-1" class="border-l-4 border-blue-500 pl-4 mb-8">
                                <h2 class="text-2xl font-bold text-gray-900 mb-4">1. Eligibility</h2>
                                <div class="space-y-3 text-gray-700">
                                    <p><strong>1.1</strong> You must be at least 13 years old to use the Platform. Users under 18 require parental or guardian consent.</p>
                                    <p><strong>1.2</strong> By registering, you warrant that all information provided (name, date of birth, gender, contact details) is true, accurate, and current.</p>
                                </div>
                            </div>

                            <!-- Section 2: Account Registration & Security -->
                            <div id="section-2" class="border-l-4 border-green-500 pl-4 mb-8">
                                <h2 class="text-2xl font-bold text-gray-900 mb-4">2. Account Registration & Security</h2>
                                <div class="space-y-3 text-gray-700">
                                    <p><strong>2.1</strong> You are responsible for maintaining the confidentiality of your account login details.</p>
                                    <p><strong>2.2</strong> You agree not to share your account or allow unauthorized use.</p>
                                    <p><strong>2.3</strong> You must promptly notify Bookletics of any unauthorized access or breach.</p>
                                </div>
                            </div>

                            <!-- Section 3: Use of the Platform -->
                            <div id="section-3" class="border-l-4 border-purple-500 pl-4 mb-8">
                                <h2 class="text-2xl font-bold text-gray-900 mb-4">3. Use of the Platform</h2>
                                <div class="space-y-3 text-gray-700">
                                    <p><strong>3.1</strong> Bookletics provides a booking platform connecting Players with sports venues and clubs ("Venues").</p>
                                    <p><strong>3.2</strong> Bookletics does not own or operate Venues and is not responsible for the quality, safety, or suitability of facilities.</p>
                                    <p><strong>3.3</strong> You agree to use the Platform only for lawful purposes and in compliance with this Agreement.</p>
                                </div>
                            </div>

                            <!-- Continue with other sections in the same pattern... -->
                            <!-- Section 4: Bookings & Payments -->
                            <div id="section-4" class="border-l-4 border-yellow-500 pl-4 mb-8">
                                <h2 class="text-2xl font-bold text-gray-900 mb-4">4. Bookings & Payments</h2>
                                <div class="space-y-3 text-gray-700">
                                    <p><strong>4.1</strong> All bookings made through the Platform are binding agreements between the Player and the Venue.</p>
                                    <p><strong>4.2</strong> Payments are processed securely via approved payment gateways. Bookletics acts as a collection agent on behalf of Venues.</p>
                                    <p><strong>4.3</strong> Refunds and cancellations are governed by our Refund & Cancellation Policy.</p>
                                </div>
                            </div>

                            <!-- Section 5: Player Conduct -->
                            <div id="section-5" class="border-l-4 border-red-500 pl-4 mb-8">
                                <h2 class="text-2xl font-bold text-gray-900 mb-4">5. Player Conduct</h2>
                                <div class="space-y-4">
                                    <div>
                                        <p class="font-semibold text-gray-800 mb-2">5.1 You agree to:</p>
                                        <ul class="list-disc list-inside space-y-1 text-gray-700 ml-4">
                                            <li>Treat other players, venue staff, and Bookletics representatives with respect.</li>
                                            <li>Follow venue rules, health & safety regulations, and fair play principles.</li>
                                            <li>Use appropriate sports attire and equipment.</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <p class="font-semibold text-gray-800 mb-2">5.2 You must not:</p>
                                        <ul class="list-disc list-inside space-y-1 text-gray-700 ml-4">
                                            <li>Engage in harassment, abuse, or discrimination.</li>
                                            <li>Damage facilities or misuse equipment.</li>
                                            <li>Commit fraudulent bookings or unlawful conduct.</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <!-- Add remaining sections 6-12 following the same pattern -->

                            <!-- Acceptance Section -->
                            <div id="acceptance-section" class="bg-blue-50 rounded-lg p-6 mt-8 border border-blue-200">
                                <h3 class="text-xl font-bold text-blue-900 mb-4">Acceptance of Terms</h3>
                                <p class="text-blue-800 mb-4">
                                    By creating an account or using the Platform, you ("User", "Player", "you") agree to these Terms. 
                                    If you do not agree, you must not use the Platform.
                                </p>
                                
                                <!-- For Mobile Apps - Acceptance Button -->
                                <div class="flex justify-center">
                                    <button
                                        mat-raised-button
                                        color="primary"
                                        class="!px-8 !py-3 !text-lg"
                                        (click)="acceptTerms()"
                                    >
                                        <mat-icon>check_circle</mat-icon>
                                        <span class="ml-2">I Accept Terms & Conditions</span>
                                    </button>
                                </div>
                            </div>

                            <!-- Contact Information -->
                        <div class="bg-gray-100 rounded-lg p-6 mt-8 text-center">
                            <h3 class="font-semibold text-gray-800 mb-2">Bookletics International FZCO</h3>
                            <p class="text-gray-600">Building A1, Dubai Digital Park, Dubai Silicon Oasis, Dubai, United Arab Emirates</p>
                            <p class="text-gray-600">Email: info&#64;bookletics.com | www.bookletics.com</p>
                        </div>
                        </div>
                    </mat-card>
                </div>
            </div>
        </mat-drawer-content>
    </mat-drawer-container>
</div>